// eDOS · Search By Department. Tiles are sourced from the eDOS Laravel
// API (`/get-edos/departments` returns the distinct, deduped, CSV-flat
// list). Selecting a tile fetches the filtered, paginated test list
// from `/get-edos/testsList?department=<name>` — counts on tiles are
// the live API total once a tile has been opened, otherwise rendered
// blank to avoid the chore of an upfront aggregate fetch.
//
// Default state (no tile selected) renders the catalogue's first page
// so the partner sees something the moment they land on the page.
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import { MP, MONO, DISPLAY } from '../medical/shared';
import { useGetDepartmentsQuery, useGetTestsListQuery } from '../../../store/edosApi';
import { mapApiToTestRow } from '../../../utils/edosLabAdapter';
import EdosResultsList from './EdosResultsList';
import EdosTestDetail from './EdosTestDetail';

const PER_PAGE = 10;

const DeptTile = ({ name, count, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      bgcolor: active ? MP.tealDark : MP.white,
      border: `1.5px solid ${active ? MP.tealDark : MP.border}`,
      borderRadius: '6px',
      p: 2,
      cursor: 'pointer',
      transition: 'all 0.16s',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        borderColor: MP.teal,
        boxShadow: active ? '0 4px 14px rgba(0,153,130,0.25)' : '0 4px 14px rgba(16,21,28,.10)',
        transform: 'translateY(-1px)',
      },
      '&::before': active ? {
        content: '""',
        position: 'absolute',
        inset: '0 0 auto 0',
        height: 3,
        bgcolor: MP.teal,
      } : undefined,
    }}
  >
    <Typography
      sx={{
        fontFamily: DISPLAY,
        fontSize: 14,
        fontWeight: 700,
        color: active ? MP.white : MP.dark,
        lineHeight: 1.25,
        letterSpacing: '0.2px',
        mb: 0.75,
        minHeight: 36,
      }}
    >
      {name}
    </Typography>
    <Stack direction="row" alignItems="baseline" spacing={0.75}>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 18,
          fontWeight: 700,
          color: active ? MP.teal : MP.tealDark,
          lineHeight: 1,
        }}
      >
        {count ?? '—'}
      </Typography>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: active ? 'rgba(255,255,255,0.7)' : MP.textMuted,
        }}
      >
        {count === 1 ? 'Test' : 'Tests'}
      </Typography>
    </Stack>
  </Box>
);

const EdosDepartments = () => {
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedTest, setSelectedTest] = useState(null);
  // Tile-name search box. Client-side filter against the API's
  // department list — no extra API call needed since the list comes
  // back fully cached on first mount.
  const [tileQ, setTileQ] = useState('');
  // In-results search box — server-side keyword filter against the
  // SELECTED department (or whole catalogue if no dept is active).
  // Debounced so each keystroke doesn't fire a request, and uses
  // ?all=1 so the results aren't paginated (the partner sees every
  // match in the category at once, not split across pages).
  const [testQ, setTestQ] = useState('');
  const [debouncedTestQ, setDebouncedTestQ] = useState('');
  // Anchor for the auto-scroll when a tile is clicked.
  const resultsRef = useRef(null);

  // Debounce the in-results search — 350 ms after the user stops
  // typing, the committed search term updates and the API fetch fires.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTestQ(testQ.trim()), 350);
    return () => clearTimeout(t);
  }, [testQ]);

  // Tile names — distinct department list from the API.
  const deptsQuery = useGetDepartmentsQuery();
  const departments = useMemo(() => deptsQuery.data || [], [deptsQuery.data]);

  // Filter tiles by the search box. Case-insensitive substring match
  // against the department name. Empty query → all tiles.
  const filteredDepts = useMemo(() => {
    const q = tileQ.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter((d) => String(d).toLowerCase().includes(q));
  }, [departments, tileQ]);

  // Test list — filtered by department when one is active, AND by
  // keyword when the in-results search has a value. When searching,
  // we pass ?all=1 so we get every matching row in one shot (no
  // pagination). RTK caches each (dept, keyword) tuple for 5 min.
  const isSearching = debouncedTestQ.length > 0;
  const listQuery = useGetTestsListQuery({
    page,
    perPage: PER_PAGE,
    ...(active ? { department: active } : {}),
    ...(isSearching ? { keyword: debouncedTestQ, all: true } : {}),
  });
  const tests = useMemo(
    () => (listQuery.data?.items || []).map(mapApiToTestRow).filter(Boolean),
    [listQuery.data],
  );
  // When searching, suppress the pagination footer (we got everything).
  const pagination = isSearching ? null : (listQuery.data?.pagination || null);

  const onTileClick = (d) => {
    setActive(active === d ? null : d);
    setPage(1);
    // Smooth-scroll to the results table so the partner sees what
    // changed without having to scroll manually. requestAnimationFrame
    // gives React a tick to render the empty results panel first
    // (useful when the query hasn't started loading yet).
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  if (selectedTest) {
    return (
      <EdosTestDetail
        test={selectedTest}
        onBack={() => setSelectedTest(null)}
        onSelectTest={setSelectedTest}
        sectionLabel="Search By Department"
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Picker hero */}
      <Box
        sx={{
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: '6px',
          boxShadow: MP.shadow,
          p: { xs: 2.5, md: 3.5 },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: MP.tealDark,
                mb: 1,
              }}
            >
              eDOS · Search By Department
            </Typography>
            <Typography
              sx={{
                fontFamily: DISPLAY,
                fontSize: { xs: 22, md: 28 },
                fontWeight: 700,
                color: MP.dark,
                letterSpacing: '0.5px',
                mb: 0.75,
              }}
            >
              {active ? active : 'Browse by laboratory department'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: MP.textMuted, maxWidth: '60ch' }}>
              {active
                ? `Showing every test that runs out of the ${active} department.`
                : 'NABL-aligned departments from our directory. Pick one to see its full test inventory with TAT, schedule, sample cutoff, and L2L pricing.'}
            </Typography>
          </Box>

          {active && (
            <Box
              onClick={() => { setActive(null); setPage(1); }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                fontFamily: MONO,
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: MP.tealDark,
                bgcolor: MP.tealLight,
                border: `1px solid ${MP.tealMid}`,
                borderRadius: '20px',
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                transition: 'all 0.14s',
                alignSelf: { xs: 'flex-start', md: 'center' },
                '&:hover': { bgcolor: MP.teal, color: MP.white, borderColor: MP.teal },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} /> All Departments
            </Box>
          )}
        </Stack>

        {/* Tile-name search box — client-side filter against the
            already-loaded department list. No extra API hit. */}
        <Box
          sx={{
            mt: 2.5,
            display: 'flex',
            alignItems: 'center',
            border: `1.5px solid ${tileQ ? MP.teal : MP.border}`,
            borderRadius: '6px',
            bgcolor: MP.bg,
            transition: 'border-color 0.18s, background 0.18s',
            '&:focus-within': { borderColor: MP.teal, bgcolor: MP.white },
          }}
        >
          <Box sx={{ pl: 1.75, display: 'flex', alignItems: 'center', color: MP.textMuted }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
          <InputBase
            value={tileQ}
            onChange={(e) => setTileQ(e.target.value)}
            placeholder="Filter departments — e.g. Biochemistry, Cytology, Package…"
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              color: MP.dark,
              px: 1.25,
              py: 1.25,
              fontFamily: DISPLAY,
            }}
          />
          {tileQ && (
            <IconButton size="small" onClick={() => setTileQ('')} sx={{ mr: 0.75 }}>
              <ClearIcon sx={{ fontSize: 16, color: MP.textMuted }} />
            </IconButton>
          )}
        </Box>

        {/* Tile grid */}
        {deptsQuery.isLoading ? (
          <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Box
                key={i}
                sx={{
                  bgcolor: MP.bg,
                  border: `1.5px solid ${MP.border}`,
                  borderRadius: '6px',
                  p: 2,
                  height: 90,
                  '@keyframes lp-pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 0.8 } },
                  animation: 'lp-pulse 1.4s ease-in-out infinite',
                }}
              />
            ))}
          </Box>
        ) : filteredDepts.length === 0 ? (
          // No tile matches the search — surface this clearly so the
          // partner doesn't think the catalogue is broken.
          <Box
            sx={{
              mt: 2.5,
              p: 3,
              textAlign: 'center',
              bgcolor: MP.bg,
              border: `1px dashed ${MP.border}`,
              borderRadius: '6px',
            }}
          >
            <Typography sx={{ fontFamily: DISPLAY, fontSize: 15, fontWeight: 700, color: MP.dark, mb: 0.5 }}>
              "{tileQ}" is not a department in our directory
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: MP.textMuted }}>
              Try a shorter keyword, or use Search By Test Name from the sidebar to look up a specific test.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 2.5,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.5,
            }}
          >
            {filteredDepts.map((d) => (
              <DeptTile
                key={d}
                name={d}
                // Count is shown live only for the active tile (we know
                // it from pagination.total). For the rest, the upfront
                // count would require an N+1 fetch — left blank to keep
                // the page snappy.
                count={active === d ? pagination?.total : null}
                active={active === d}
                onClick={() => onTileClick(d)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Results scroll target — onTileClick scrolls to this anchor.
          scrollMarginTop accounts for the sticky topbar so the results
          panel doesn't slide under it. */}
      <Box ref={resultsRef} sx={{ scrollMarginTop: 80 }}>
        {/* In-results test search — server-side keyword filter scoped
            to the active department. ?all=1 returns every match (no
            pagination) so the partner sees the full hit list. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1.5px solid ${testQ ? MP.teal : MP.border}`,
            borderRadius: '6px',
            bgcolor: testQ ? MP.white : MP.bg,
            mb: 1.5,
            transition: 'border-color 0.18s, background 0.18s',
            '&:focus-within': { borderColor: MP.teal, bgcolor: MP.white },
          }}
        >
          <Box sx={{ pl: 1.75, display: 'flex', alignItems: 'center', color: MP.textMuted }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
          <InputBase
            value={testQ}
            onChange={(e) => setTestQ(e.target.value)}
            placeholder={
              active
                ? `Search tests in ${active} — name, code, or parameter…`
                : 'Search tests across the catalogue — name, code, or parameter…'
            }
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              color: MP.dark,
              px: 1.25,
              py: 1.25,
              fontFamily: DISPLAY,
            }}
          />
          {testQ && (
            <IconButton size="small" onClick={() => setTestQ('')} sx={{ mr: 0.75 }}>
              <ClearIcon sx={{ fontSize: 16, color: MP.textMuted }} />
            </IconButton>
          )}
        </Box>

        <EdosResultsList
          tests={tests}
          loading={listQuery.isLoading || listQuery.isFetching}
          error={listQuery.isError ? listQuery.error : null}
          pagination={pagination}
          onPageChange={setPage}
          onSelectTest={setSelectedTest}
          emptyTitle={
            isSearching
              ? (active
                  ? `"${debouncedTestQ}" — not matched in ${active}`
                  : `"${debouncedTestQ}" — no tests match`)
              : (active ? `No tests live under ${active} yet` : 'No tests in directory')
          }
          emptySub={
            isSearching
              ? (active
                  ? `No tests in the ${active} department contain "${debouncedTestQ}". Clear the search to see all ${active} tests, or pick a different department.`
                  : `Try a shorter keyword, or pick a department / specialty to narrow the search.`)
              : 'More tests roll out as departments come online — this is the v2.1 inventory snapshot.'
          }
        />
      </Box>
    </Stack>
  );
};

export default EdosDepartments;
